import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { membersInvitation } from '@excelway/constants/taggychips.config';
import { QueryModel } from '@excelway/models/api/query';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { CommonService } from 'app/common-dialogs/common.services';
import { AdminConsoleService } from 'app/modules/admin-console/admin-console.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'settings-team-common',
  templateUrl: './team.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsTeamComponent implements OnInit {
  users: any[] = [];
  roles: any[];
  memberForm: FormGroup;
  workspaceUsers: any[] = [];
  oldAccessLevel: string;
  invitedUsers: any[] = [];
  usersRequestData: { id: string; email: string; redirectTo: string }[] = [];
  returnUrl = encodeURIComponent(window.location.href);
  invitedUserGroups: any[] = [];
  membersInvitation = membersInvitation;
  teams: any[] = [];
  objects: any[];
  directRelations: any[] = [];

  /**
   * Constructor
   */
  constructor(
    private formBuilder: FormBuilder,
    private _changeDetectorRef: ChangeDetectorRef,
    private _commonService: CommonService,
    private toastr: ToastrService,
    private _adminConsoleService: AdminConsoleService,
    private _fuseConfirmationService: FuseConfirmationService,
    @Inject(MAT_DIALOG_DATA)
    public data
  ) {
    this.memberForm = this.formBuilder.group({
      role: ['read', [Validators.required]],
    });
  }
  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Setup the roles
    this.roles = [
      {
        label: 'Facilitator',
        value: 'admin',
        description:
          'Can create, edit, animate and delete workshop as well as manage workshop members.',
      },
      {
        label: 'Participant',
        value: 'write',
        description: 'Can create cards, vote, and comment.',
      },
    ];
    this.loadWorkspaceUsers();
    this.getWorkshopAndUsers();
    // this.loadTeams();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  loadTeams = (): void => {
    if (this.data.context) {
      this._adminConsoleService
        .getTeamsWithMemberCounts(this.data.context)
        .subscribe(teams => {
          this.teams = teams.map(team => ({
            id: team.id,
            name: team.name,
            createdAt: team.createdAt,
            members: team.memberCount,
            color: team.color,
          }));
          this.mergeObjects();
          this._changeDetectorRef.detectChanges();
        });
    }
  };

  captureOldAccessLevel(role: string): void {
    this.oldAccessLevel = role;
  }

  onAdd(event: { value: any }): void {
    if (event.value.email) {
      this.invitedUsers.push(event.value);
    } else {
      this.invitedUserGroups.push(event.value);
    }
  }

  onRemove(event: { value: any }): void {
    if (event.value.email) {
      this.invitedUsers = this.invitedUsers.filter(
        user => user.email !== event.value.email
      );
    } else {
      this.invitedUserGroups = this.invitedUserGroups.filter(
        userGroup => userGroup.id !== event.value.id
      );
    }
  }

  invite = (): void => {
    if (this.invitedUserGroups.length > 0 && this.invitedUsers.length > 0) {
      this.toastr.error("You can't invite both teams and users.");
      return;
    } else if (this.invitedUserGroups.length > 0) {
      this.inviteUserGroups();
    } else if (this.invitedUsers.length > 0) {
      this.inviteUsers();
    } else {
      this.toastr.warning('No users or teams to invite.');
    }
  };

  async inviteUserGroups(): Promise<void> {
    this._commonService
      .checkUserGroupsMemberRelations({
        roleTypeId: this.data.id,
        roleTypeName: 'Workshop',
        userGroups: this.invitedUserGroups,
      })
      .subscribe((res: any) => {
        this.directRelations = res;

        // If no direct relations, proceed to invitation process directly
        if (this.directRelations.length === 0) {
          this.processInvitations();
          return;
        }

        // If there are direct relations, show confirmation dialog
        const emails = this.directRelations
          .map((relation: any) => relation.email)
          .join(' / ');
        const confirmation = this._fuseConfirmationService.open({
          title: 'Membres Déja Présents',
          message: `Les utilisateurs ( ${emails} ) sont déjà associés à cet atelier.
              En ajoutant via cette équipe, leurs roles individuels seront remplacés par ceux définis pour l'équipe.`,
          actions: {
            confirm: {
              label: 'Confirmer',
            },
            cancel: {
              show: true,
              label: 'Annuler',
            },
          },
        });

        confirmation.afterClosed().subscribe(async (confirmed: string) => {
          if (confirmed === 'confirmed') {
            this.processInvitations();
          }
        });
      });
  }

  private async processInvitations(): Promise<void> {
    const body = {
      roleTypeId: this.data.id,
      roleTypeName: 'Workshop',
      userGroups: this.invitedUserGroups,
      role: this.memberForm.get('role')?.value,
      context: this.data.context,
      defaultRedirection: window.location.href,
    };

    await this._commonService
      .addMultipleUserGroups(body)
      .subscribe(async res => {
        await this._commonService.inviteUsers(
          res.users,
          this.data.id,
          'Workshop',
          this.data.context,
          this.memberForm.get('role')?.value
        );
      });

    this.memberForm.reset();
    this.invitedUserGroups.forEach(userGroup =>
      this.onRemove({ value: userGroup })
    );
    this.getWorkshopAndUsers();
    this._changeDetectorRef.detectChanges();
  }

  loadWorkspaceUsers = (): void => {
    const query: QueryModel = {
      roleType: 'Workspace',
      id: this.data.context,
      fields: ['id', 'name'],
      relations: [
        {
          relation: 'User',
          fields: ['id', 'name', 'email'],
        },
      ],
    };

    this._commonService.getObjectAndUsers(query).subscribe(user => {
      this.workspaceUsers = this.removeDuplicateUsers(user.User);
      this.mergeObjects();
    });
  };

  mergeObjects(): void {
    this.objects = [...this.teams, ...this.workspaceUsers];
  }

  removeDuplicateUsers = (users: any[]): any[] => {
    const uniqueUsers = users.reduce((acc, current) => {
      const x = acc.find(user => user.email === current.email);
      if (!x) {
        return acc.concat([current]);
      } else {
        return acc;
      }
    }, []);
    return uniqueUsers;
  };
  getWorkshopAndUsers(): any {
    this._commonService.getObjectMembers(this.data.id).subscribe(
      user => {
        this.users = user;
        this._changeDetectorRef.markForCheck();
        this.markSoleAdmin();
      },
      error => {
        console.error('Error', error);
      }
    );
  }

  updateMember(userId: string, role: string): any {
    const memberData = {
      roleTypeId: this.data.id,
      userId: userId,
      newAccessLevel: role,
      oldAccessLevel: this.oldAccessLevel,
    };
    this._commonService.updateMemberRole(memberData).subscribe(() => {
      this.toastr.success('Role updated successfully');
      this.getWorkshopAndUsers();
      this._changeDetectorRef.detectChanges();
    });
  }

  removeMember(userId: string, roleTypeId: string, role: string): any {
    const removeMemberData = {
      userId: userId,
      roleTypeId: roleTypeId,
      role: role,
    };
    this._commonService.removeMemberRelation(removeMemberData).subscribe(() => {
      this.users = this.users.filter(user => user.id !== userId);
      this.getWorkshopAndUsers();
      this._changeDetectorRef.markForCheck();
    });
  }

  openDeleteConfirmation(
    userId: string,
    roleTypeId: string,
    role: string
  ): void {
    // Open the confirmation dialog
    const confirmation = this._fuseConfirmationService.open({
      title: 'Delete member',
      message:
        'Are you sure you want to delete this member ? This action cannot be undone!',
      actions: {
        confirm: {
          label: 'Delete',
        },
      },
    });

    // Subscribe to the confirmation dialog closed action
    confirmation.afterClosed().subscribe((confirmed: string) => {
      if (confirmed === 'confirmed') {
        this.removeMember(userId, roleTypeId, role);
      }
    });
  }

  async inviteUsers(): Promise<void> {
    const alreadyInUsers = this.invitedUsers.some(invitedUser =>
      this.users.some(existingUser => existingUser.email === invitedUser.email)
    );

    if (alreadyInUsers) {
      this.toastr.error('One or more users are already in the project.');
      this.memberForm.reset();
      return;
    }
    await this._commonService.inviteUsers(
      this.invitedUsers,
      this.data.id,
      'Workshop',
      this.data.context,
      this.memberForm.get('role')?.value
    );
    this.memberForm.reset();
    this.invitedUsers.forEach(user => this.onRemove({ value: user }));
    this.getWorkshopAndUsers();
    this._changeDetectorRef.detectChanges();
  }

  markSoleAdmin(): void {
    const admins = this.users.filter(user => user.role === 'admin');
    this.users = this.users.map(user => ({
      ...user,
      soleAdmin: user.role === 'admin' && admins.length === 1,
    }));
  }

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }
}
